import Api from "@/api";
import {
  getBrowserFingerprintData,
  getFingerDevice,
} from "@/utils/fingerprint";

import dayjs from "./dayjs.min";

const WINDOWS_1252_BYTE_MAP = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
};

function decodeExportFileName(fileName) {
  let decodedFileName = fileName.trim().replace(/^['"]|['"]$/g, "");
  try {
    decodedFileName = decodeURIComponent(decodedFileName);
  } catch (error) {
    // 非 URL 编码文件名继续按原值处理
  }

  const bytes = [];
  for (const character of decodedFileName) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0xff) {
      bytes.push(codePoint);
    } else if (WINDOWS_1252_BYTE_MAP[codePoint] !== undefined) {
      bytes.push(WINDOWS_1252_BYTE_MAP[codePoint]);
    } else {
      return decodedFileName;
    }
  }

  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(
      Uint8Array.from(bytes),
    );
  } catch (error) {
    return decodedFileName;
  }
}

function getExportFileName(response) {
  const disposition = response.headers.get("content-disposition") || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const normalMatch = disposition.match(/filename="?([^";]+)"?/i);
  const responseFileName = utf8Match?.[1] || normalMatch?.[1];
  if (responseFileName) {
    return decodeExportFileName(responseFileName);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/pdf")) return "导出文件.pdf";
  if (contentType.includes("application/msword")) return "导出文件.doc";
  if (contentType.includes("wordprocessingml.document")) return "导出文件.docx";
  if (contentType.includes("application/vnd.ms-excel")) return "导出文件.xls";
  if (contentType.includes("spreadsheetml.sheet")) return "导出文件.xlsx";
  return "导出文件.xlsx";
}

export default {
  // token 过期处理
  pastDue(vm) {
    vm.$router.push({ path: "/login" });
    localStorage.removeItem("token");
  },

  // 导出dayJS
  dayjs: dayjs,

  // 千分位格式化（保留两位小数）
  threeNumForTwo(num) {
    const reg = /\d(?=(?:\d{3})+\b)/g;
    num = parseFloat(num).toFixed(2);
    const result = num.toString().replace(reg, "$&,");
    if (result !== "0.00" && result !== 0) {
      return result;
    }
    return 0;
  },

  // 对象转 GET 请求参数
  objToGet(obj) {
    let str = "";
    if (Object.prototype.toString.call(obj) === "[object Object]") {
      for (const key in obj) {
        if (str === "") {
          str += `?${key}=${obj[key]}`;
        } else {
          str += `&${key}=${obj[key]}`;
        }
      }
    }
    return str;
  },

  // 通用导出（带 token）
  async publicExport(vm, url, params, openType) {
    let str = "?";
    if (Object.prototype.toString.call(params) === "[object Object]") {
      for (const key in params) {
        if (str === "?") {
          str += `${key}=${encodeURIComponent(params[key])}`;
        } else {
          str += `&${key}=${encodeURIComponent(params[key])}`;
        }
      }
      if (str !== "?") str += "&";
    }
    const baseUrl = process.env.VUE_APP_URL || "";
    const fullUrl =
      baseUrl + url + str + "token=" + localStorage.getItem("token");
    try {
      const [{ fingerprint }, fingerDevice] = await Promise.all([
        getBrowserFingerprintData(),
        getFingerDevice(),
      ]);
      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "finger-device": fingerDevice,
          fingerprint,
        },
      });
      if (!response.ok) {
        throw new Error(`导出请求失败：${response.status}`);
      }
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const result = await response.json();
        throw new Error(result.msg || "导出接口返回异常");
      }

      const fileUrl = URL.createObjectURL(await response.blob());
      const downloadLink = document.createElement("a");
      downloadLink.href = fileUrl;
      downloadLink.download = getExportFileName(response);
      if (openType === 1) downloadLink.target = "_blank";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    } catch (error) {
      if (vm && vm.$message) vm.$message.error("导出失败，请稍后重试");
      console.error("导出请求失败:", error);
    }
  },

  // 打开文件/图片预览
  openFile(url) {
    const preUrl = Array.isArray(url) ? url.join(",") : url;
    const suffix = preUrl.substring(preUrl.lastIndexOf("."));
    if (suffix === ".pdf") {
      window.open(preUrl);
    } else {
      window.open(preUrl);
    }
  },

  // 生成 UUID
  generateUUID() {
    let d = new Date().getTime();
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      },
    );
    return uuid;
  },

  // 检测设备类型
  detectDeviceType() {
    const userAgent = navigator.userAgent;
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent,
      );
    const isTablet =
      /iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
    if (isMobile) return "Mobile";
    if (isTablet) return "Tablet";
    return "Desktop";
  },

  // 获取API基础地址
  getApiUrl(defaultHost, from) {
    let siteConfig = JSON.parse(localStorage.getItem("siteConfig"));
    let key = ["upload"].indexOf(from) != -1 ? from : "host";
    if (siteConfig && siteConfig.hasOwnProperty(key)) {
      return siteConfig.host;
    }
    if (key != "host" && siteConfig && siteConfig.hasOwnProperty("host")) {
      return siteConfig.host;
    }
    return defaultHost;
  },

  // 获取上传地址
  getUploadUrl(defaultHost) {
    let url = this.getApiUrl(defaultHost, "upload");
    return url;
  },

  
};
